/**
 * <related-rail> — lazy tabs for the product page collection rails
 * (sections/related-collections-rail.liquid: "More Headsets" and the price
 * rail) and, with "Load other tabs after the page" on, for Hyper's own
 * featured-collection rails on the home page. Tab switching stays with the
 * theme's product-tabs element, each panel's carousel with featured-collection;
 * this script only fetches cards.
 *
 * Every rail renders its first tab with the page (the product rails can turn
 * that off, which leaves a shimmer until the fetch lands and read as slow;
 * featured-collection's "Load this rail after the page" does the same on
 * purpose for the rails stacked below the fold of the collection pages, where
 * the shimmer is off screen and the saving is a hundred-odd cards of HTML
 * and upgrade work per page).
 * Every other tab's track is empty and carries data-rail-lazy="<collection
 * url>" inside a <template> that product-tabs clones on open. Cards come from
 * sections/related-rail-panel.liquid over the Section Rendering API, are
 * filtered (the page's own product out), trimmed to data-rail-cards and
 * appended wherever the track lives now; a live Swiper is told to re-measure.
 *
 * ORDER: nothing is fetched until the HTML is parsed and the main thread has
 * an idle slot, or the visitor scrolls or touches the page, whichever comes
 * first (waiting for `load` was tried 2026-10-19: with the product page's
 * third-party scripts it can be many seconds in, and a tab clicked before it
 * opened to its loading state). Then one request at a time through a queue
 * shared by every rail on the page: each rail's first tab first (in page
 * order), then every other tab, in page order, whether or not the rail is in
 * view. Hover, touch or focus on a tab, or opening it, fetches that tab at
 * once, ahead of the queue, and so does a rail coming within 800px of the
 * viewport.
 *
 * MOUNT AHEAD: once a template-held tab's cards and first-row images are in,
 * its panel is cloned into the page hidden (display: none) in idle time. The
 * first insert of a panel is the expensive part, not the fetch: twelve cards'
 * custom elements upgrade, Swiper initialises and Klaviyo renders a star
 * widget into every card, all synchronously inside the insert, before the
 * browser can paint, while product-tabs has already hidden the previous
 * panel. That read as a second of white on the first open (2026-10-19,
 * confirmed in the console: cards and images all complete at open). Paid
 * ahead, product-tabs finds the panel live and only un-hides it; the Swiper,
 * initialised at zero width, is re-measured on open.
 *
 * IMAGES: the first row's images of a template-held tab are primed as
 * soon as its cards land (and again on intent, in case they landed before
 * the rail was watched), so the clone paints from cache instead of a white
 * row; a live panel's images load on their own (loading="lazy").
 *
 * The product rails' heading and "See all" link follow the active tab
 * (product-tabs raises `tabChange`), and every tab click is published on
 * Shopify.analytics as `rail_tab_click`.
 */
if (!customElements.get('related-rail')) {
  // ---- Page-wide loader queue ------------------------------------------------
  const queue = { high: [], low: [] };
  let pageReady = false;
  let running = false;

  const pump = () => {
    if (!pageReady || running) return;
    const job = queue.high.shift() || queue.low.shift();
    if (!job) return;
    running = true;
    Promise.resolve()
      .then(job)
      .catch(() => {})
      .then(() => {
        running = false;
        pump();
      });
  };

  const enqueue = (job, priority) => {
    queue[priority].push(job);
    pump();
  };

  const go = () => {
    if (pageReady) return;
    pageReady = true;
    pump();
  };
  const markReady = () => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(go, { timeout: 1000 });
    } else {
      setTimeout(go, 300);
    }
  };
  if (document.readyState !== 'loading') {
    markReady();
  } else {
    document.addEventListener('DOMContentLoaded', markReady, { once: true });
  }
  // A visitor already moving is a visitor about to reach the rails.
  ['scroll', 'touchstart', 'pointerdown', 'keydown'].forEach((type) => {
    window.addEventListener(type, go, { once: true, passive: true });
  });

  class RelatedRail extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('.tabs__btn[data-index]'));
      this.panels = this.querySelector('.tabs__panels');
      this.headingCollection = this.querySelector('[data-rail-heading-collection]');
      this.headingPrefix = this.querySelector('[data-rail-heading-prefix]');
      this.seeAll = this.querySelector('[data-rail-see-all]');
      this.cardsPerTab = parseInt(this.dataset.railCards, 10) || 12;
      this.excludeHandle = this.dataset.railExclude || '';
      // Which rail this is (the rank metafield it reads), for analytics.
      this.railName = this.dataset.railName || 'rail_rank';
      this.loads = new Map();
      this.activeIndex = 0;

      if (!this.panels) return;

      // Every panel index, live or templated. A single-tab rail has no tab
      // strip, so the panels, not the buttons, are the source of truth.
      this.indices = Array.from(this.panels.querySelectorAll(':scope > .tabs__panel, :scope > template'))
        .map((node) => Number(node.dataset.index))
        .filter((index) => !Number.isNaN(index));

      this.addEventListener('tabChange', (event) => this.onTabChange(event.detail.selectedIndex));

      this.tabs.forEach((tab) => {
        const index = Number(tab.dataset.index);
        const warm = () => this.want(index);
        tab.addEventListener('pointerenter', warm, { passive: true });
        tab.addEventListener('touchstart', warm, { passive: true });
        tab.addEventListener('focus', warm);
      });

      // The tab on show first, after load, ahead of every rail's other tabs;
      // then the rest, so they are in before anyone scrolls down to them.
      if (this.indices.length) enqueue(() => this.ensureLoaded(this.indices[0]), 'high');
      this.indices.slice(1).forEach((index) => enqueue(() => this.ensureLoaded(index), 'low'));

      // A rail coming into view jumps the queue: on a collection page with
      // ten lazy rails the page-order queue would reach the one being looked
      // at seconds late. 800px of lead so it is usually in before it shows.
      if ('IntersectionObserver' in window && this.indices.length) {
        this.viewport = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.viewport.disconnect();
            this.want(this.indices[0]);
          },
          { rootMargin: '800px 0px' }
        );
        this.viewport.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.viewport) this.viewport.disconnect();
    }

    // Intent on a tab: fetch now, and prime its first row while it still sits
    // in its template, so the clone on click paints from cache.
    want(index) {
      this.ensureLoaded(index).then(() => this.primeIfTemplated(index));
    }

    // Only once the cards are in: a failed fetch must not mark the empty
    // track primed, or mount an empty panel, or the retry could never prime.
    primeIfTemplated(index) {
      const track = this.trackFor(index);
      if (!track || !track.dataset.railLoaded || track.isConnected || track.dataset.railPrimed) return;
      track.dataset.railPrimed = 'true';
      this.primeImages(track, 8);
      this.scheduleMount(index);
    }

    // Clone a loaded tab's panel into the page, hidden, while the main thread
    // is idle, so the custom element upgrades, Swiper init and Klaviyo widgets
    // run now rather than inside the click. product-tabs then finds the panel
    // live and only un-hides it.
    scheduleMount(index) {
      const mount = () => this.mountHidden(index);
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(mount, { timeout: 4000 });
      } else {
        setTimeout(mount, 1000);
      }
    }

    mountHidden(index) {
      if (this.panels.querySelector(`.tabs__panel[data-index="${index}"]`)) return;
      const template = this.panels.querySelector(`template[data-index="${index}"]`);
      const source = template && template.content.firstElementChild;
      if (!source) return;
      const track = source.querySelector('[data-rail-lazy]');
      if (track && !track.dataset.railLoaded) return;
      const panel = source.cloneNode(true);
      panel.hidden = true;
      this.panels.appendChild(panel);
    }

    // Browsers fetch nothing for images that are not in the document, so a
    // template-held tab used to open to empty image boxes. Prime the first row
    // (what a desktop viewport shows) with Image objects carrying the same
    // sizes/srcset, and mark those images eager so the clone never re-defers.
    primeImages(track, count) {
      this.primed = this.primed || [];
      Array.from(track.children)
        .slice(0, count)
        .forEach((card) => {
          card.querySelectorAll('img').forEach((source) => {
            if (source.loading === 'lazy') source.loading = 'eager';
            const src = source.getAttribute('src');
            if (!src) return;
            const image = new Image();
            // sizes before srcset, so the candidate is chosen for the real slot.
            if (source.getAttribute('sizes')) image.sizes = source.getAttribute('sizes');
            if (source.getAttribute('srcset')) image.srcset = source.getAttribute('srcset');
            image.src = src;
            this.primed.push(image);
          });
        });
    }

    // The lazy track for a tab, wherever it lives right now: the live panel once
    // product-tabs has cloned it, else the inert <template> content. The track
    // keeps its data-rail-lazy marker after its cards land (data-rail-loaded
    // says so), so it can still be found: dropping the marker on load made
    // this return null for the priming step, and the images were never
    // fetched until the tab was opened (the white row on first open).
    trackFor(index) {
      const live = this.panels.querySelector(`.tabs__panel[data-index="${index}"] [data-rail-lazy]`);
      if (live) return live;
      const template = this.panels.querySelector(`template[data-index="${index}"]`);
      return template ? template.content.querySelector('[data-rail-lazy]') : null;
    }

    /**
     * Fetches a tab's cards once. A failed fetch is forgotten, so it is retried
     * on the next intent, open or queue pass; a landed one is remembered on the
     * track as well as here.
     */
    ensureLoaded(index) {
      if (this.loads.has(index)) return this.loads.get(index);
      const track = this.trackFor(index);
      if (!track) return Promise.resolve();
      const url = track.dataset.railLazy;
      if (!url || track.dataset.railLoaded) return Promise.resolve();

      const separator = url.includes('?') ? '&' : '?';
      const load = fetch(`${url}${separator}section_id=related-rail-panel`, { credentials: 'same-origin' })
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`rail tab ${response.status}`))))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          let items = Array.from(doc.querySelectorAll('[data-product-handle]'));
          if (this.excludeHandle) {
            items = items.filter((item) => item.dataset.productHandle !== this.excludeHandle);
          }
          items = items.slice(0, this.cardsPerTab);

          // Re-resolve: the panel may have been cloned out of its template while
          // the fetch was in flight.
          const target = this.trackFor(index) || track;
          // The panel marks every card a slide; a track that is a plain grid
          // (featured-collection with too few products for a slider) must not
          // get Swiper's slide sizing.
          if (!target.classList.contains('swiper-wrapper')) {
            items.forEach((item) => item.classList.remove('swiper-slide'));
          }
          items.forEach((item) => target.appendChild(item));
          target.dataset.railLoaded = 'true';

          const panel = target.closest('.tabs__panel');
          if (panel) panel.classList.remove('is-loading');

          // Still in its template: fetch the first row's images now, so the
          // tab opens to pictures rather than a white row.
          this.primeIfTemplated(index);

          // Cards landed in a live, already-initialised carousel: re-measure so
          // the arrows, progress bar and paging see the new slides.
          const carousel = target.closest('featured-collection');
          const slider = carousel && carousel.isConnected && carousel.sliderInstance && carousel.sliderInstance.slider;
          if (slider && typeof slider.update === 'function') slider.update();
        })
        .catch(() => {
          this.loads.delete(index);
        });

      this.loads.set(index, load);
      return load;
    }

    onTabChange(index) {
      this.activeIndex = index;
      this.remeasure(index);
      const tab = this.tabs[index];
      if (!tab) return;
      const label = tab.dataset.label || tab.textContent.trim();
      // The heading's name for the collection: the tab label ("Headsets") on
      // the related rail, the collection title ("Headsets Under $200") on the
      // price rail, where the tab only says "Under $200".
      const heading = tab.dataset.heading || label;
      // Only the accent is red: "More [Headsets]", "Headsets [$200–$300]".
      if (this.headingPrefix && tab.dataset.headingPrefix !== undefined) {
        this.headingPrefix.textContent = tab.dataset.headingPrefix;
      }
      if (this.headingCollection) this.headingCollection.textContent = tab.dataset.headingAccent || heading;
      if (this.seeAll) {
        if (tab.dataset.url) this.seeAll.setAttribute('href', tab.dataset.url);
        // "See all Headsets" follows the tab too.
        const text = this.seeAll.querySelector('.btn__text') || this.seeAll;
        const prefix = this.seeAll.dataset.railSeeAllPrefix || '';
        text.textContent = `${prefix} ${heading}`.trim();
      }
      this.ensureLoaded(index);
      this.track(index, label, tab.dataset.url);
    }

    // A panel mounted hidden initialised its Swiper at zero width; now that
    // product-tabs has shown it, re-measure.
    remeasure(index) {
      const panel = this.panels.querySelector(`.tabs__panel[data-index="${index}"]`);
      const carousel = panel && panel.querySelector('featured-collection');
      const slider = carousel && carousel.sliderInstance && carousel.sliderInstance.slider;
      if (slider && typeof slider.update === 'function') slider.update();
    }

    // One custom event per tab click on Shopify's analytics bus, so a custom
    // pixel (Settings > Customer events) can forward it to GA4 and answer
    // whether anyone clicks tab three, or the price rail at all. product-tabs
    // only raises tabChange on a click, never at load.
    track(index, label, url) {
      const bus = window.Shopify && window.Shopify.analytics;
      if (!bus || typeof bus.publish !== 'function') return;
      try {
        bus.publish('rail_tab_click', {
          rail: this.railName,
          tab_index: index,
          tab_label: label,
          collection_url: url || '',
          product_handle: this.excludeHandle,
        });
      } catch (error) {
        /* analytics must never break the rail */
      }
    }
  }

  customElements.define('related-rail', RelatedRail);
}
